import type { GameAction, PlayIntent, ReduceContext } from './actions';
import { isInterrupt, isPlayable, isRescue } from './card';
import type { CardTitle, CardUid, PlayerId } from './ids';
import { reduce } from './reducer';
import { canAct, type GameState, type PlayerState } from './state';
import { cardName } from './cardText';
import { t } from '../i18n';

/**
 * **合法动作枚举** —— 「当前局面下能做什么」。
 *
 * 这是**规则概念**，不是开发工具的私货：机器人用它找路，
 * 热座 UI 用它把可选项渲染成按钮，将来的联机层也能用它做本地校验。
 *
 * ## 做法：逐个试
 *
 * 每条候选都真的喂一遍 `reduce`，接受且**局面指纹变了**才算数。
 *
 * 之所以不手写一份"合法性规则"，是因为那样等于把规则写两遍 ——
 * 两份迟早对不上，而 reducer 才是唯一权威。
 *
 * ⚠️ 代价是 O(候选数) 次 reducer 调用。一局最多几十个候选，
 * 每次状态变化算一遍完全够用；真成瓶颈再上缓存。
 *
 * ⚠️ 为什么要比指纹：有些动作是**幂等**的（重复发返回成功但不改状态）——
 * 不过滤的话机器人会一直选它、永远不动，看起来就像卡死了。
 *
 * ⚠️ `ctx` 必须显式传进来 —— `core/` 不认识任何具体数据包。
 */

/** 动作的分类标签，方便机器人和 UI 挑选 */
export type OptionTag =
  | 'draw'
  | 'pass'
  | 'close'
  | 'nope'
  | 'play-single'
  | 'play-pair'
  | 'play-triple'
  | 'play-five'
  | 'decide-rescue-yes'
  | 'decide-rescue-no'
  | 'decide-insert'
  | 'decide-give'
  | 'decide-pick'
  /** 两张同名：挑一个位次去抽（见 `Decision` 的 `stealCard`） */
  | 'decide-steal';

export interface Option {
  readonly label: string;
  readonly action: GameAction;
  readonly tag: OptionTag;
  /** 单张出牌时这张牌是什么；其它情况为 null */
  readonly cardTitle: CardTitle | null;
  /**
   * **动作本身没写明、但界面要知道的那张手牌。**
   *
   * 目前只有一处：`useRescue` 的动作（`{ kind: 'useRescue', use: true }`）
   * 说的是"用救命牌"，而救命牌是**手里哪一张**由规则层算（可能有两张）。
   * 界面要能把"把这张 Catch 拖出去"翻译成这个动作（见
   * `ui/screens/tableIntent.ts` 的 `cardsOf`），所以在这里带上。
   *
   * ⚠️ 其它动作的用牌是**动作自带**的（`turn/play` / `window/nope` /
   *    `giveCard`），别往这里塞重复信息。
   */
  readonly handCard?: CardUid;
}

/** 给玩家看的牌名。查不到就退回内部 ID */
export function describeCard(state: GameState, uid: CardUid, ctx: ReduceContext): string {
  const title = state.cards[uid]?.title;
  if (title === undefined) return t('core.card.unknown', { uid });
  const def = ctx.registry.get(title);
  return def === undefined ? `#${uid} ${title}` : cardName(def);
}

/**
 * 一个动作"用到了哪几张手牌"。
 *
 * UI 用它做**按选中牌过滤**：选了 2 张同名，就只显示"一对"那两个选项，
 * 而不是把几十个候选一股脑铺出来。
 */
export function cardsUsedBy(action: GameAction): readonly CardUid[] {
  switch (action.t) {
    case 'turn/play':
      switch (action.play.kind) {
        case 'single':
          return [action.play.card];
        case 'pair':
        case 'triple':
          return [...action.play.cards];
        case 'fiveDifferent':
          return [...action.play.cards];
      }
      return [];
    case 'window/nope':
      return [action.card];
    case 'decide':
      return action.decision.kind === 'giveCard' || action.decision.kind === 'pickFromDiscard'
        ? [action.decision.card]
        : [];
    default:
      return [];
  }
}

/** 局面指纹 —— 用来识别「合法但什么都没发生」的动作 */
export function signature(state: GameState): string {
  return [
    state.phase,
    state.turn?.player ?? '-',
    state.turn?.turnsRemaining ?? 0,
    state.turn?.fromAttack === true ? 'a' : 'n',
    state.drawPile.length,
    state.discardPile.length,
    state.players.map((p) => `${p.alive ? 1 : 0}${p.hand.length}`).join(','),
    state.resolution === null ? '-' : `R${state.resolution.nopeChain.length}`,
    state.decision === null ? '-' : `D${state.decision.kind}`,
  ].join('|');
}

/**
 * 列出当前局面下 `actor` 所有**真的会改变局面**的合法动作。
 *
 * ⚠️ 返回空数组是**异常信号**：说明对局卡住了
 * （`dev/simulate.ts` 把它当致命违规）。
 */
export function options(state: GameState, actor: PlayerId, ctx: ReduceContext): Option[] {
  const out: Option[] = [];
  const before = signature(state);
  const push = (
    label: string,
    action: GameAction,
    tag: OptionTag,
    card?: CardTitle,
    handCard?: CardUid,
  ): void => {
    const result = reduce(state, action, ctx);
    if (result.ok && signature(result.state) !== before) {
      out.push({
        label,
        action,
        tag,
        cardTitle: card ?? null,
        ...(handCard === undefined ? {} : { handCard }),
      });
    }
  };

  if (state.phase !== 'playing') return out;

  const me = state.players.find((p) => p.id === actor);
  if (me === undefined) return out;

  const name = (uid: CardUid): string => describeCard(state, uid, ctx);

  // ---- 待抉择 ----
  if (state.decision !== null && state.decision.player === actor) {
    const d = state.decision;
    if (d.kind === 'useRescue') {
      /**
       * ⚠️ 救命牌是**手里哪一张**由规则层算（见 `isRescue`），动作里没有它 ——
       *    所以这里把它带上（`Option.handCard`），否则界面没法把"拖那张 Catch
       *    出去"翻成这个动作，玩家就只能"放弃"了。
       */
      push(
        t('core.option.useRescue'),
        { t: 'decide', actor, decision: { kind: 'useRescue', use: true } },
        'decide-rescue-yes',
        undefined,
        rescueInHand(state, me, ctx),
      );
      push(
        t('core.option.giveUp'),
        { t: 'decide', actor, decision: { kind: 'useRescue', use: false } },
        'decide-rescue-no',
      );
    } else if (d.kind === 'insertLethal') {
      // 位置用「从底下数第 n 位」表述：0 = 最底下，length = 最顶上
      for (let i = 0; i <= state.drawPile.length; i++) {
        const where =
          i === state.drawPile.length
            ? t('core.option.insertTop')
            : i === 0
              ? t('core.option.insertBottom')
              : t('core.option.insertNth', { n: i });
        push(
          t('core.option.insertTo', { where }),
          { t: 'decide', actor, decision: { kind: 'insertLethal', index: i } },
          'decide-insert',
        );
      }
    } else if (d.kind === 'giveCard') {
      for (const uid of me.hand) {
        push(
          t('core.option.giveCard', { card: name(uid) }),
          { t: 'decide', actor, decision: { kind: 'giveCard', card: uid } },
          'decide-give',
        );
      }
      // ★ 手里有 [断点] 也可以否决这次索取（用户要求）
      for (const uid of me.hand) {
        const title = state.cards[uid]?.title;
        const def = title === undefined ? undefined : ctx.registry.get(title);
        if (def !== undefined && isInterrupt(def)) {
          push(t('core.option.nope', { card: name(uid) }), { t: 'window/nope', actor, card: uid }, 'nope');
        }
      }
    } else if (d.kind === 'stealCard') {
      for (let i = 0; i < d.count; i++) {
        push(
          t('core.option.stealNth', { n: i + 1 }),
          { t: 'decide', actor, decision: { kind: 'stealCard', index: i } },
          'decide-steal',
        );
      }
    } else if (d.kind === 'pickFromDiscard') {
      for (const uid of d.candidates) {
        push(
          t('core.option.pickFromDiscard', { card: name(uid) }),
          { t: 'decide', actor, decision: { kind: 'pickFromDiscard', card: uid } },
          'decide-pick',
        );
      }
    }
    return out;
  }

  // ★ **被抽的人**：手上有 [断点] 就能否决这次抽牌（用户要求）。
  //   他是 `decision.from`，不是 `decision.player`，所以单开一支。
  if (state.decision !== null && state.decision.kind === 'stealCard' && state.decision.from === actor) {
    for (const uid of me.hand) {
      const title = state.cards[uid]?.title;
      const def = title === undefined ? undefined : ctx.registry.get(title);
      if (def !== undefined && isInterrupt(def)) {
        push(t('core.option.nope', { card: name(uid) }), { t: 'window/nope', actor, card: uid }, 'nope');
      }
    }
    return out;
  }

  // ---- 打断窗口：只有"打 [断点]"这一条 ----
  // 关窗由房主按出牌动画内部发（`window/timeout`），不作为玩家选项。
  if (state.resolution !== null) {
    for (const uid of me.hand) {
      push(t('core.option.interrupt', { card: name(uid) }), { t: 'window/nope', actor, card: uid }, 'nope');
    }
    // 房主能关窗。⚠️ 这是**内部**动作：UI 不渲染它（见 `tableIntent`），
    //    但规则层要认，否则"房主没牌可打断"会被误判成死局。
    if (actor === state.host) push(t('core.option.closeWindow'), { t: 'window/timeout', actor }, 'close');
    return out;
  }

  // ---- 正常回合 ----
  if (!canAct(state, actor)) return out;

  const others = state.players.filter((p) => p.id !== actor && p.alive);

  // 单张。需要目标的牌要额外出一份带目标的选项
  for (const uid of me.hand) {
    const title = state.cards[uid]?.title;
    const def = title === undefined ? undefined : ctx.registry.get(title);
    // ★ **不能主动打出的牌不提供"单张"选项**（如 [断点]：只能打断，或当连击材料）。
    //   以前不挡，于是 options() 里冒出一条 reducer 必拒的 play-single ——
    //   拖出去就被打回，看起来像"无效操作"（用户报的）。连击那条路不受影响。
    if (def === undefined || !isPlayable(def)) continue;
    if (def.traits.needsTarget === true) {
      for (const other of others) {
        push(
          t('core.option.playSingleTarget', { card: name(uid), target: other.name }),
          { t: 'turn/play', actor, play: { kind: 'single', card: uid, target: other.id } },
          'play-single',
          title,
        );
      }
    } else {
      push(
        t('core.option.playSingle', { card: name(uid) }),
        { t: 'turn/play', actor, play: { kind: 'single', card: uid } },
        'play-single',
        title,
      );
    }
  }

  // 连击：按牌名分组
  const byTitle = new Map<CardTitle, CardUid[]>();
  for (const uid of me.hand) {
    const title = state.cards[uid]?.title;
    if (title === undefined) continue;
    const list = byTitle.get(title) ?? [];
    list.push(uid);
    byTitle.set(title, list);
  }

  for (const [title, uids] of byTitle) {
    const [a, b, c] = uids;
    if (a !== undefined && b !== undefined) {
      for (const other of others) {
        push(
          t('core.option.playPair', { card: name(a), target: other.name }),
          { t: 'turn/play', actor, play: { kind: 'pair', cards: [a, b], target: other.id } },
          'play-pair',
          title,
        );
      }
    }
    if (a !== undefined && b !== undefined && c !== undefined) {
      for (const other of others) {
        for (const named of ctx.registry.cards) {
          push(
            t('core.option.playTriple', { card: name(a), target: other.name, named: cardName(named) }),
            {
              t: 'turn/play',
              actor,
              play: { kind: 'triple', cards: [a, b, c], target: other.id, namedTitle: named.id },
            },
            'play-triple',
            title,
          );
        }
      }
    }
  }

  if (state.config.combos.fiveDifferent && me.hand.length >= 5) {
    const picked: CardUid[] = [];
    const seen = new Set<CardTitle>();
    for (const uid of me.hand) {
      const title = state.cards[uid]?.title;
      if (title === undefined || seen.has(title)) continue;
      seen.add(title);
      picked.push(uid);
      if (picked.length === 5) break;
    }
    const [p0, p1, p2, p3, p4] = picked;
    if (
      p0 !== undefined &&
      p1 !== undefined &&
      p2 !== undefined &&
      p3 !== undefined &&
      p4 !== undefined
    ) {
      for (const pick of state.discardPile) {
        const play: PlayIntent = {
          kind: 'fiveDifferent',
          cards: [p0, p1, p2, p3, p4],
          discardPick: pick,
        };
        push(t('core.option.playFive', { card: name(pick) }), { t: 'turn/play', actor, play }, 'play-five');
      }
    }
  }

  push(t('core.option.draw'), { t: 'turn/draw', actor }, 'draw');
  return out;
}

/**
 * **手里那张救命牌**（有两张就取第一张）。
 *
 * 只有 `useRescue` 那一支用得到，而它必须和规则层挑的是同一张 ——
 * `reducer.ts` 里是 `findInHand(..., isRescue)`，同一个 trait、同一个顺序。
 */
function rescueInHand(
  state: GameState,
  me: PlayerState,
  ctx: ReduceContext,
): CardUid | undefined {
  return me.hand.find((uid) => {
    const title = state.cards[uid]?.title;
    const def = title === undefined ? undefined : ctx.registry.get(title);
    return def !== undefined && isRescue(def);
  });
}
